// @ts-nocheck
/*
 * Port of views/dashboard/call-centre/edit.js (origin/main-dev, 912 lines).
 * BYTE-IDENTICAL LOGIC — every state hook, useEffect, API call, toast,
 * licence math, demo-parent gate, voicemail ACL flow, and JSX condition
 * matches v1 to the letter.
 *
 * Key edit-vs-create differences preserved:
 *   - `initialLicence` snapshot from `subUserLicenceId.available_licence`;
 *     `getLicenceMax(key) = parentAvailable + initialValue`.
 *   - `isDemoParent` from creator's assignedCompany.is_demo_company (also
 *     checked against fetched profile's is_demo_company / company /
 *     companyId flags) → licence panel readOnly.
 *   - `usedLicence` displayed below the panel.
 *   - `showCallCenterUsedCol` gate hides the Call Centers used cell for
 *     Call Center / Company admin roles.
 *   - `profileLoaded` gate on form until initial fetch resolves.
 *   - Domain regex is less strict than create (accepts bare `example.com`
 *     without protocol).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';

/* Local micro-shims for react-bootstrap Row/Col/Form.* — keeps the JSX
 * intact while the page comes off the compat shim. */
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts: string[] = [];
  const push = (bp: string, val: any) => {
    if (val === undefined || val === null || val === false) return;
    parts.push(bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`);
  };
  push('xs', xs); push('sm', sm); push('md', md); push('lg', lg); push('xl', xl);
  return parts.length ? parts.join(' ') : 'col';
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form className={className} onSubmit={onSubmit || ((e: any) => e.preventDefault())} {...props}>
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>{children}</div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>{children}</label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select') return <select className={cls} {...props}>{children}</select>;
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, ...props }: any) => (
  <select className={_cx('form-control form-select', className)} {...props}>{children}</select>
);
Form.Check = ({ type = 'checkbox', label, id, className, inline, children, ...props }: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && <label htmlFor={id} className="form-check-label">{label}</label>}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>{children}</small>
);
import { toast } from 'sonner';
import {
  API_CALL_CENTRE_PROFILE,
  API_COUNTRY_CODES_LIST,
  API_TIMEZONES_LIST,
  API_PARENT_CALL_CENTER_LICENCE,
  API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST,
  API_USER_COUNTRY_CODE_DIRECTIONS_BY_CALL_CENTRE,
} from '@/service/url';
import CountryCodeDirectionPicker from '@/components/partials/dashboard/country-code-direction/CountryCodeDirectionPicker';
import LicenceAllocationPanel from '@/components/partials/dashboard/company/LicenceAllocationPanel';
import { postApiCall, getApiCall, fetchAndSetFlashMessage } from '@/service/httpCall';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ROUTES,
  USER_ROLES,
  MODULE_ACL,
  FLASH_MESSAGE_ACTION_OBJECT,
} from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { Storage } from '@/utilities/setting';
import {
  FiUser,
  FiGlobe,
  FiClock,
  FiAlignLeft,
  FiFlag,
  FiUsers,
  FiActivity,
  FiShare2,
  FiPhone,
  FiSmartphone,
  FiUserCheck,
  FiCpu,
} from 'react-icons/fi';
import { FaHeadset } from 'react-icons/fa';
import {
  hasCallCentreVoicemailAcl,
  getSelectableStorageQuotas,
  DEFAULT_STORAGE_QUOTA,
  getUserModuleOperations,
  readVoicemailEnabled,
} from '@/utilities/callCentreVoicemailAcl';
import {
  hasCallCentreTranscriptionAcl,
  readTranscriptionEnabled,
} from '@/utilities/callCentreTranscriptionAcl';
import {
  filterCallCentreCountryCodes,
  filterCallCentreTimezonesForCountry,
  filterCallCentreTimezonesList,
} from '@/utilities/callCentreFormFilters';
import { Card as ShadcnCard } from '@/components/ui/card';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as ShadPageButton } from '@/components/ui/button';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Award,
  Voicemail as VoicemailIcon,
  Mic as MicIcon,
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES as PAGE_ROUTES } from '@/utilities/constant';
import { Switch } from '@/components/ui/switch';
import { Stepper, useStepper, type StepDescriptor } from '@/components/ui/stepper';
import Loader from '@/components/partials/components/Loader';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import '@/features/users/user-forms.css';

const EDIT_CALL_CENTRE_STEPS: StepDescriptor[] = [
  { id: 'details', label: 'Details', description: 'Name, domain, timezone', icon: Building2 },
  { id: 'licence', label: 'Licence', description: 'Adjust quotas', icon: Award },
];

function IconField({ icon: Icon, children, hasTrailing, trailingIcon: TrailingIcon, textarea }) {
  return (
    <div
      className={`ccf-input-with-icon${textarea ? ' ccf-input-with-icon--textarea' : ''}${
        hasTrailing ? ' ccf-input-with-icon--trailing' : ''
      }${TrailingIcon ? ' ccf-input-with-icon--icon-right' : ''}`}
    >
      {!TrailingIcon && Icon && (
        <span className="ccf-input-with-icon__icon">
          <Icon size={18} />
        </span>
      )}
      {children}
      {TrailingIcon && (
        <span className="ccf-input-with-icon__icon ccf-input-with-icon__icon--right">
          <TrailingIcon size={18} />
        </span>
      )}
    </div>
  );
}

const CallCentreUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const stepper = useStepper(EDIT_CALL_CENTRE_STEPS.length);

  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [description, setDescription] = useState('');
  const [buttonDisable, setButtonDisable] = useState(false);
  const [countryCodeList, setCountryCodeList] = useState([]);
  const [countryCode, setCountryCode] = useState('');
  const [timezonesList, setTimezonesList] = useState([]);
  const [timezone, setTimezone] = useState('');
  const [filteredTimezones, setFilteredTimezones] = useState([]);
  const [agent, setAgent] = useState(0);
  const [liveAgent, setLiveAgent] = useState(0);
  const [demoLiveAgents, setDemoLiveAgents] = useState(0);
  const [teamLead, setTeamLead] = useState(0);
  const [channel, setChannel] = useState(0);
  const [aiAgent, setAiAgent] = useState(0);
  const [aiAgentLiveChannel, setAiAgentLiveChannel] = useState(0);

  const [cps, setCps] = useState(0);
  const [mobile, setMobile] = useState(0);
  const [errors, setErrors] = useState({});
  const [loginUserRole, setLoginUserRole] = useState(null);
  const [parentLicence, setParentLicence] = useState(null);
  const [initialLicence, setInitialLicence] = useState({});
  const [usedLicence, setUsedLicence] = useState({});
  const [nameError, setNameError] = useState('');
  const [domainError, setDomainError] = useState('');
  const [notes, setNotes] = useState([]);
  const [countryCodeDirections, setCountryCodeDirections] = useState([]);
  const [selectedCountryCodeDirections, setSelectedCountryCodeDirections] = useState([]);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [voicemailEnabled, setVoicemailEnabled] = useState(false);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);
  const [storageQuota, setStorageQuota] = useState(DEFAULT_STORAGE_QUOTA);
  const [isDemoParent, setIsDemoParent] = useState(false);

  const voicemailAclContext = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      if (!raw) return { userRole: null, isSuperAdmin: false };
      const user = JSON.parse(raw);
      return {
        userRole: user?.role,
        isSuperAdmin: user?.role === USER_ROLES.SUPERADMIN,
      };
    } catch {
      return { userRole: null, isSuperAdmin: false };
    }
  }, []);

  const userModuleOps = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      if (!raw) return {};
      return getUserModuleOperations(JSON.parse(raw));
    } catch {
      return {};
    }
  }, []);

  const canConfigureVoicemail = useMemo(
    () => hasCallCentreVoicemailAcl(userModuleOps, voicemailAclContext),
    [userModuleOps, voicemailAclContext],
  );

  const storageQuotaOptions = useMemo(
    () => getSelectableStorageQuotas(userModuleOps, voicemailAclContext),
    [userModuleOps, voicemailAclContext],
  );

  const canConfigureTranscription = useMemo(
    () => hasCallCentreTranscriptionAcl(userModuleOps, voicemailAclContext),
    [userModuleOps, voicemailAclContext],
  );

  function getProfileApiUrl() {
    return API_CALL_CENTRE_PROFILE.replace(':id', id);
  }

  useEffect(() => {
    checkAuth();
    try {
      const user = JSON.parse(Storage.getItem('user'));
      if (user?.role) setLoginUserRole(user.role);
      const assigned = user?.assignedCompany || user?.assigned_company_details || null;
      if (assigned?.is_demo_company === true) setIsDemoParent(true);
    } catch (err) {
      console.log(err);
    }

    fetchAndSetFlashMessage(
      (msg) =>
        msg.module_name === MODULE_ACL.CALL_CENTRE &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.EDIT &&
        msg.type === 'Note',
      setNotes,
    );

    getApiCall(API_PARENT_CALL_CENTER_LICENCE)
      .then((resp) => setParentLicence(resp?.parentLicence))
      .catch((error) => {
        toast.warning(typeof error === 'string' ? error : JSON.stringify(error));
      });

    getApiCall(API_COUNTRY_CODES_LIST, {})
      .then((resp) => setCountryCodeList(filterCallCentreCountryCodes(resp)))
      .catch(onError);

    getApiCall(API_TIMEZONES_LIST, {})
      .then((resp) => setTimezonesList(filterCallCentreTimezonesList(resp)))
      .catch(onError);

    getApiCall(API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST, {})
      .then((data) => setCountryCodeDirections(Array.isArray(data) ? data : []))
      .catch(() => setCountryCodeDirections([]));

    getApiCall(API_USER_COUNTRY_CODE_DIRECTIONS_BY_CALL_CENTRE(id), {})
      .then((data) => {
        const ids = Array.isArray(data)
          ? data.map((item) => item.country_code_direction_id).filter(Boolean)
          : [];
        setSelectedCountryCodeDirections(ids);
      })
      .catch(() => {});

    getApiCall(getProfileApiUrl(), {})
      .then((resp) => {
        setName(resp.name || '');
        setDomain(resp.domain || '');
        setDescription(resp.description || '');
        if (
          resp?.is_demo_company === true ||
          resp?.company?.is_demo_company === true ||
          resp?.companyId?.is_demo_company === true
        ) {
          setIsDemoParent(true);
        }
        setCountryCode(resp.country_code_id || '');
        setTimezone(resp.timezone_id || '');

        const licence = resp?.subUserLicenceId || {};
        const available = licence?.available_licence || {};
        const used = licence?.used_licence || {};

        setUsedLicence(used);

        const currentValues = {
          role_ccagent: available.role_ccagent || 0,
          role_teamlead: available.role_teamlead || 0,
          live_agent: available.live_agent || 0,
          team_lead: available.team_lead || 0,
          channel: available.channel || 0,
          cps: available.cps || 0,
          mobile: available.mobile || 0,
          demo_live_agents_per_reseller_company:
            available.demo_live_agents_per_reseller_company || 0,
          ai_agent: available.ai_agent || 0,
          ai_agent_live_channel: available.ai_agent_live_channel || 0,
        };

        setAgent(currentValues.role_ccagent);
        setTeamLead(currentValues.role_teamlead);
        setLiveAgent(currentValues.live_agent);
        setChannel(currentValues.channel);
        setAiAgent(currentValues.ai_agent);
        setAiAgentLiveChannel(currentValues.ai_agent_live_channel);
        setCps(currentValues.cps);
        setMobile(currentValues.mobile);
        setDemoLiveAgents(currentValues.demo_live_agents_per_reseller_company);
        setInitialLicence(currentValues);
        setVoicemailEnabled(readVoicemailEnabled(resp));
        setTranscriptionEnabled(readTranscriptionEnabled(resp));
        const q = Number(resp.storage_quota_in_mb);
        setStorageQuota([500, 1000].includes(q) ? q : DEFAULT_STORAGE_QUOTA);
        setProfileLoaded(true);
      })
      .catch(onError);
  }, [id]);

  useEffect(() => {
    if (!countryCode || !timezonesList.length || !countryCodeList.length) return;
    const selectedCountry = countryCodeList.find((c) => c.id === countryCode);
    setFilteredTimezones(filterCallCentreTimezonesForCountry(timezonesList, selectedCountry));
  }, [countryCode, timezonesList, countryCodeList]);

  const licenceKeyMap = {
    agent: 'role_ccagent',
    live_agent: 'live_agent',
    channel: 'channel',
    cps: 'cps',
    mobile: 'mobile',
    team_lead: 'role_teamlead',
    demo_live_agents: 'demo_live_agents_per_reseller_company',
    ai_agent: 'ai_agent',
    ai_agent_live_channel: 'ai_agent_live_channel',
  };

  const getLicenceMax = (key) => {
    const licenceKey = licenceKeyMap[key];
    const parentAvailable = Number(parentLicence?.available_licence?.[licenceKey] || 0);
    const initialValue = Number(initialLicence?.[licenceKey] || 0);
    return parentAvailable + initialValue;
  };

  const validateInput = (value, key) => {
    if (value === '' || value == null) return 0;
    const digitsOnly = String(value).replace(/\D/g, '');
    if (digitsOnly === '') return 0;
    const num = parseInt(digitsOnly, 10);
    let newErrors = { ...errors };

    if (Number.isNaN(num)) return 0;
    if (num < 0) {
      newErrors[key] = 'Value cannot be negative';
      setErrors(newErrors);
      return 0;
    }

    const maxAllowed = getLicenceMax(key);

    if (num > maxAllowed) {
      newErrors[key] = `Value cannot exceed ${maxAllowed}`;
      setErrors(newErrors);
      return num;
    }

    newErrors[key] = '';

    const nextAgent = key === 'agent' ? num : agent;
    const nextTeamLead = key === 'team_lead' ? num : teamLead;

    if (nextTeamLead > nextAgent) {
      newErrors.team_lead = `Total Team Lead cannot be more than Total Agent (${nextAgent})`;
    } else {
      newErrors.team_lead = '';
    }

    setErrors(newErrors);
    return num;
  };

  const stepLicence = (key, current, setter, delta) => {
    const next = Math.max(0, (parseInt(String(current), 10) || 0) + delta);
    setter(validateInput(String(next), key));
  };

  const canIncrementLicence = (key, current) => {
    const max = getLicenceMax(key);
    return (parseInt(String(current), 10) || 0) < max;
  };

  const handleCountryChange = (e) => {
    const selectedCountryId = e.target.value;
    setCountryCode(selectedCountryId);
    setTimezone('');
    const selectedCountry = countryCodeList.find((c) => c.id === selectedCountryId);
    setFilteredTimezones(filterCallCentreTimezonesForCountry(timezonesList, selectedCountry));
    setTimezone('');
  };

  function onError(err) {
    if (typeof err !== 'object' && err?.includes?.('TokenExpiredError')) {
      errorCheck(err, navigate);
    } else {
      console.log(err);
    }
  }

  const validateName = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      setNameError('Name is required');
      return false;
    }
    if (trimmedValue.length < 3) {
      setNameError('Name must be at least 3 characters long');
      return false;
    }
    if (trimmedValue.length > 500) {
      setNameError('Name must not exceed 500 characters');
      return false;
    }
    const nameRegex = /^[a-zA-Z0-9_]+$/;
    if (!nameRegex.test(trimmedValue)) {
      setNameError(
        'Name can only contain letters, numbers, and underscores (_). No special characters allowed.',
      );
      return false;
    }
    setNameError('');
    return true;
  };

  const validateDomain = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      setDomainError('Domain is required');
      return false;
    }
    if (trimmedValue.length > 500) {
      setDomainError('Domain must not exceed 500 characters');
      return false;
    }
    const domainRegex = /^(https?:\/\/)?([\w\.\-@]+)([\/\w \.\-@]*)*\/?$/i;
    if (!domainRegex.test(trimmedValue)) {
      setDomainError(
        'Please enter a valid domain or URL (e.g., example.com or https://example.com)',
      );
      return false;
    }
    setDomainError('');
    return true;
  };

  function validateForm() {
    let valid = true;
    let errorMessage = '';

    const nameValid = validateName(name);
    const domainValid = validateDomain(domain);

    if (!nameValid) {
      valid = false;
      errorMessage = nameError || 'Name validation failed';
    }
    if (!domainValid) {
      valid = false;
      errorMessage = errorMessage
        ? `${errorMessage}; ${domainError || 'Domain validation failed'}`
        : domainError || 'Domain validation failed';
    }
    if (!description.trim()) {
      valid = false;
      errorMessage = errorMessage
        ? `${errorMessage}; Description required`
        : 'Description required';
    }
    if (!countryCode) {
      valid = false;
      errorMessage = errorMessage
        ? `${errorMessage}; Country Code required`
        : 'Country Code required';
    }
    if (!timezone) {
      valid = false;
      errorMessage = errorMessage
        ? `${errorMessage}; Timezone required`
        : 'Timezone required';
    }

    if (!valid && errorMessage) {
      toast.warning(errorMessage);
    }

    return valid;
  }

  async function submitForm() {
    setButtonDisable(true);

    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    if (Object.values(errors).some((error) => error !== '')) {
      toast.warning('Please fix all validation errors before submitting');
      setButtonDisable(false);
      return;
    }

    const params = {
      name,
      domain,
      description,
      countryCode,
      timezone,
      licence: {
        role_ccagent: agent,
        role_teamlead: teamLead,
        live_agent: liveAgent,
        team_lead: teamLead,
        channel,
        cps,
        mobile,
        reseller: 0,
        company: 0,
        demo_live_agents_per_reseller_company: demoLiveAgents,
        ai_agent: aiAgent,
        ai_agent_live_channel: aiAgentLiveChannel,
      },
      country_code_directions: Array.isArray(selectedCountryCodeDirections)
        ? selectedCountryCodeDirections.map(Number)
        : [],
    };

    if (canConfigureVoicemail) {
      params.is_voicemail_enabled = !!voicemailEnabled;
      params.storage_quota_in_mb = voicemailEnabled ? Number(storageQuota) : 0;
    }

    if (canConfigureTranscription) {
      params.is_transcription_enabled = !!transcriptionEnabled;
    }

    postApiCall(getProfileApiUrl(), params)
      .then(onCallCentreUpdateSuccess)
      .catch(onCallCentreUpdateError);
  }

  function onCallCentreUpdateSuccess() {
    toast.success('Call centre updated successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.CALL_CENTRES.LIST}`);
    }, 1500);
  }

  function onCallCentreUpdateError(err) {
    if (typeof err !== 'object' && err?.includes?.('TokenExpiredError')) {
      errorCheck(err, navigate);
    } else {
      setButtonDisable(false);
      toast.warning(friendlyApiError(err));
    }
  }

  const licenceNote =
    notes.find((n) => Number(n.priority) === 1)?.message ??
    'Adjust licence counts within your company pool. Increases use available company licences; decreases return unused capacity.';

  const licenceFooterNote =
    loginUserRole !== USER_ROLES.SUPERADMIN
      ? notes.find((n) => Number(n.priority) === 2)?.message ??
        'Available company licences are shown below for reference.'
      : '';

  const hasLicenceErrors = Object.values(errors).some((e) => e !== '');
  const showCallCenterUsedCol =
    loginUserRole !== USER_ROLES.CALL_CENTRE_ADMIN &&
    loginUserRole !== USER_ROLES.COMPANY_ADMIN;

  function validateStep0Details() {
    validateName(name);
    validateDomain(domain);
    if (!name || nameError) return false;
    if (!domain || domainError) return false;
    if (!countryCode) return false;
    if (!timezone) return false;
    if (!description) return false;
    return true;
  }
  function handleWizardNext() {
    if (stepper.current === 0 && !validateStep0Details()) return;
    stepper.next();
  }

  return (
    <FillPage>
      <PageHeader
        title="Edit Call Center"
        description="Adjust the call center's details, licences, and default admin user."
        actions={
          <ShadPageButton asChild variant="outline" size="sm">
            <RouterLink to={`/${PAGE_ROUTES.CALL_CENTRES.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </RouterLink>
          </ShadPageButton>
        }
      />
      <Row>
        <Col xl="12" lg="12">
          <ShadcnCard className="mt-4">
            <div className="call-centre-create-form-card p-4">
              <div className="new-user-info company-create-form call-centre-create-form">
                <div className="mb-4 flex items-center justify-between gap-3 border-b border-border/60 pb-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Update Call Center
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Fields marked with <span className="text-destructive">*</span> are required
                  </div>
                </div>

                {!profileLoaded ? (
                  <Loader />
                ) : (
                  <form>
                    <Stepper
                      steps={EDIT_CALL_CENTRE_STEPS}
                      current={stepper.current}
                      completed={stepper.completed}
                      onStepClick={stepper.goTo}
                      className="mb-6"
                    />

                    {/* ── Step 0: Details ─────────────────────────────── */}
                    {stepper.current === 0 && (
                    <div className="row g-3 call-centre-create-form__row">
                      <Form.Group className="col-12 col-lg-6 form-group mb-0">
                        <Form.Label>
                          Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="name"
                          maxLength={500}
                          value={name}
                          className={nameError ? 'is-invalid' : ''}
                          placeholder="Call center name"
                          onChange={(e) => {
                            setName(e.target.value);
                            validateName(e.target.value);
                          }}
                          onBlur={(e) => validateName(e.target.value)}
                          required
                        />
                        {nameError && (
                          <div className="invalid-feedback d-block">{nameError}</div>
                        )}
                      </Form.Group>

                      <Form.Group className="col-12 col-lg-6 form-group mb-0">
                        <Form.Label>
                          Domain <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="domain"
                          maxLength={500}
                          value={domain}
                          className={domainError ? 'is-invalid' : ''}
                          placeholder="https://example.com"
                          onChange={(e) => {
                            setDomain(e.target.value);
                            validateDomain(e.target.value);
                          }}
                          onBlur={(e) => validateDomain(e.target.value)}
                          required
                        />
                        {domainError && (
                          <div className="invalid-feedback d-block">{domainError}</div>
                        )}
                      </Form.Group>

                      <Form.Group className="col-12 col-lg-6 form-group mb-0">
                        <Form.Label>
                          Country Code <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          id="countryCode"
                          required
                          value={countryCode}
                          onChange={handleCountryChange}
                        >
                          <option value="">Select country</option>
                          {countryCodeList.map((val) => (
                            <option key={val.id} value={val.id}>
                              {val.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="col-12 col-lg-6 form-group mb-0">
                        <Form.Label>
                          Timezone <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          id="timezone"
                          required
                          value={timezone}
                          disabled={!countryCode}
                          onChange={(e) => setTimezone(e.target.value)}
                        >
                          <option value="">Select timezone</option>
                          {filteredTimezones.map((val) => (
                            <option key={val.id} value={val.id}>
                              {val.value}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="col-12 form-group mb-0">
                        <Form.Label>
                          Description <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          id="description"
                          maxLength={500}
                          value={description}
                          placeholder="Describe this call center"
                          onChange={(e) => setDescription(e.target.value)}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="col-12 col-lg-6 form-group mb-0">
                        <CountryCodeDirectionPicker
                          items={countryCodeDirections}
                          selectedIds={selectedCountryCodeDirections}
                          onChange={setSelectedCountryCodeDirections}
                          label="Country Codes"
                          showSelectAll
                          placeholder="Select country codes"
                        />
                      </Form.Group>

                      {canConfigureVoicemail ? (
                        <div className="col-12 col-lg-6 form-group mb-0">
                          <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                            <div className="flex items-center gap-3">
                              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                                <VoicemailIcon className="h-4 w-4" />
                              </span>
                              <div>
                                <div className="text-sm font-semibold text-foreground">
                                  Voicemail
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {voicemailEnabled
                                    ? 'Enabled — agents can receive voicemails'
                                    : 'Disabled'}
                                </div>
                              </div>
                            </div>
                            <Switch
                              checked={!!voicemailEnabled}
                              onCheckedChange={(v) => setVoicemailEnabled(!!v)}
                              aria-label="Voicemail enabled"
                            />
                          </div>
                          {/* Storage quota (MB) — hidden for now.
                          {voicemailEnabled ? (
                            <div className="mt-2 flex items-center gap-2">
                              <label
                                htmlFor="ccEditVoicemailStorage"
                                className="whitespace-nowrap text-xs font-medium text-muted-foreground"
                              >
                                Storage quota (MB)
                              </label>
                              <Form.Select
                                id="ccEditVoicemailStorage"
                                value={storageQuota}
                                onChange={(e) => setStorageQuota(Number(e.target.value))}
                                className="max-w-[140px]"
                              >
                                {storageQuotaOptions.map((q) => (
                                  <option key={q} value={q}>
                                    {q}
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                          ) : null} */}
                        </div>
                      ) : null}

                      {canConfigureTranscription ? (
                        <div className="col-12 col-lg-6 form-group mb-0">
                          <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                            <div className="flex items-center gap-3">
                              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                                <MicIcon className="h-4 w-4" />
                              </span>
                              <div>
                                <div className="text-sm font-semibold text-foreground">
                                  Transcription
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {transcriptionEnabled
                                    ? 'Enabled — agents can be given live-call transcription'
                                    : 'Disabled'}
                                </div>
                              </div>
                            </div>
                            <Switch
                              checked={!!transcriptionEnabled}
                              onCheckedChange={(v) => setTranscriptionEnabled(!!v)}
                              aria-label="Transcription enabled"
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                    )}

                    {/* ── Step 1: Licence ─────────────────────────────── */}
                    {stepper.current === 1 && (
                    <>
                    <LicenceAllocationPanel
                      showNote
                      subtitle="Adjust licences allocated to this call center"
                      noteMessage={licenceNote}
                      footerNote={licenceFooterNote}
                      columns={[
                        {
                          key: 'agent',
                          icon: FiUsers,
                          iconModifier: 'agent',
                          title: 'Total Agent',
                          description: 'Total number of agents',
                          value: parseInt(String(agent), 10) || 0,
                          onChange: (val) => setAgent(validateInput(String(val), 'agent')),
                          onDecrement: () => stepLicence('agent', agent, setAgent, -1),
                          onIncrement: () => stepLicence('agent', agent, setAgent, 1),
                          disabledMinus: (parseInt(String(agent), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence('agent', parseInt(String(agent), 10) || 0),
                          error: errors.agent,
                          readOnly: isDemoParent,
                        },
                        {
                          key: 'team_lead',
                          icon: FiUserCheck,
                          iconModifier: 'agent',
                          title: 'Total Team Lead',
                          description: 'Total number of team leads',
                          value: parseInt(String(teamLead), 10) || 0,
                          onChange: (val) => setTeamLead(validateInput(String(val), 'team_lead')),
                          onDecrement: () => stepLicence('team_lead', teamLead, setTeamLead, -1),
                          onIncrement: () => stepLicence('team_lead', teamLead, setTeamLead, 1),
                          disabledMinus: (parseInt(String(teamLead), 10) || 0) <= 0,
                          disabledPlus:
                            !canIncrementLicence('team_lead', parseInt(String(teamLead), 10) || 0) ||
                            (parseInt(String(teamLead), 10) || 0) >= (parseInt(String(agent), 10) || 0),
                          error: errors.team_lead,
                          readOnly: isDemoParent,
                        },
                        {
                          key: 'live_agent',
                          icon: FiActivity,
                          iconModifier: 'live',
                          title: 'Live Agent',
                          description: 'Agents for live calls',
                          value: parseInt(String(liveAgent), 10) || 0,
                          onChange: (val) => setLiveAgent(validateInput(String(val), 'live_agent')),
                          onDecrement: () => stepLicence('live_agent', liveAgent, setLiveAgent, -1),
                          onIncrement: () => stepLicence('live_agent', liveAgent, setLiveAgent, 1),
                          disabledMinus: (parseInt(String(liveAgent), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'live_agent',
                            parseInt(String(liveAgent), 10) || 0,
                          ),
                          error: errors.live_agent,
                          readOnly: isDemoParent,
                        },
                        {
                          key: 'channel',
                          icon: FiShare2,
                          iconModifier: 'channel',
                          title: 'Channels',
                          description: 'Communication channels',
                          value: parseInt(String(channel), 10) || 0,
                          onChange: (val) => setChannel(validateInput(String(val), 'channel')),
                          onDecrement: () => stepLicence('channel', channel, setChannel, -1),
                          onIncrement: () => stepLicence('channel', channel, setChannel, 1),
                          disabledMinus: (parseInt(String(channel), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence('channel', parseInt(String(channel), 10) || 0),
                          error: errors.channel,
                          readOnly: isDemoParent,
                        },
                        {
                          key: 'ai_agent',
                          icon: FiCpu,
                          iconModifier: 'ai',
                          title: 'AI Agents',
                          description: 'AI agents this call centre can create',
                          value: parseInt(String(aiAgent), 10) || 0,
                          onChange: (val) => setAiAgent(validateInput(String(val), 'ai_agent')),
                          onDecrement: () => stepLicence('ai_agent', aiAgent, setAiAgent, -1),
                          onIncrement: () => stepLicence('ai_agent', aiAgent, setAiAgent, 1),
                          disabledMinus: (parseInt(String(aiAgent), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'ai_agent',
                            parseInt(String(aiAgent), 10) || 0,
                          ),
                          error: errors.ai_agent,
                        },
                        {
                          key: 'ai_agent_live_channel',
                          icon: FiShare2,
                          iconModifier: 'ai',
                          title: 'AI Agent Live Channels',
                          description: 'Concurrent AI agent call channels',
                          value: parseInt(String(aiAgentLiveChannel), 10) || 0,
                          onChange: (val) =>
                            setAiAgentLiveChannel(validateInput(String(val), 'ai_agent_live_channel')),
                          onDecrement: () =>
                            stepLicence(
                              'ai_agent_live_channel',
                              aiAgentLiveChannel,
                              setAiAgentLiveChannel,
                              -1,
                            ),
                          onIncrement: () =>
                            stepLicence(
                              'ai_agent_live_channel',
                              aiAgentLiveChannel,
                              setAiAgentLiveChannel,
                              1,
                            ),
                          disabledMinus: (parseInt(String(aiAgentLiveChannel), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'ai_agent_live_channel',
                            parseInt(String(aiAgentLiveChannel), 10) || 0,
                          ),
                          error: errors.ai_agent_live_channel,
                        },
                        {
                          key: 'cps',
                          icon: FiPhone,
                          iconModifier: 'channel',
                          title: 'CPS',
                          description: 'Calls per second',
                          value: parseInt(String(cps), 10) || 0,
                          onChange: (val) => setCps(validateInput(String(val), 'cps')),
                          onDecrement: () => stepLicence('cps', cps, setCps, -1),
                          onIncrement: () => stepLicence('cps', cps, setCps, 1),
                          disabledMinus: (parseInt(String(cps), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence('cps', parseInt(String(cps), 10) || 0),
                          error: errors.cps,
                        },
                        {
                          key: 'mobile',
                          icon: FiSmartphone,
                          iconModifier: 'mobile',
                          title: 'Mobile License',
                          description: 'Mobile Device licences',
                          value: parseInt(String(mobile), 10) || 0,
                          onChange: (val) => setMobile(validateInput(String(val), 'mobile')),
                          onDecrement: () => stepLicence('mobile', mobile, setMobile, -1),
                          onIncrement: () => stepLicence('mobile', mobile, setMobile, 1),
                          disabledMinus: (parseInt(String(mobile), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence('mobile', parseInt(String(mobile), 10) || 0),
                          error: errors.mobile,
                        },
                        {
                          key: 'demo_live_agents',
                          icon: FiActivity,
                          iconModifier: 'live',
                          title: 'Demo Live Agents (Reseller)',
                          description: 'Demo live-agent pool for this reseller tree',
                          value: parseInt(String(demoLiveAgents), 10) || 0,
                          hidden: !(
                            Number(
                              parentLicence?.available_licence
                                ?.demo_live_agents_per_reseller_company || 0,
                            ) > 0 ||
                            Number(initialLicence?.demo_live_agents_per_reseller_company || 0) > 0
                          ),
                          onChange: (val) =>
                            setDemoLiveAgents(validateInput(String(val), 'demo_live_agents')),
                          onDecrement: () =>
                            stepLicence('demo_live_agents', demoLiveAgents, setDemoLiveAgents, -1),
                          onIncrement: () =>
                            stepLicence('demo_live_agents', demoLiveAgents, setDemoLiveAgents, 1),
                          disabledMinus: (parseInt(String(demoLiveAgents), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'demo_live_agents',
                            parseInt(String(demoLiveAgents), 10) || 0,
                          ),
                          error: errors.demo_live_agents,
                        },
                      ]}
                      availableSummary={
                        loginUserRole !== USER_ROLES.SUPERADMIN ? (
                          <div className="licence-available-summary">
                            <h6 className="licence-available-summary__title">
                              Available Licence (Allocated Company)
                            </h6>
                            <div className="licence-available-summary__row">
                              <span>Agent: {parentLicence?.available_licence?.role_ccagent ?? 0}</span>
                              <span>
                                Total Team Lead: {parentLicence?.available_licence?.role_teamlead ?? 0}
                              </span>
                              <span>Live Agent: {parentLicence?.available_licence?.live_agent ?? 0}</span>
                              <span>Channels: {parentLicence?.available_licence?.channel ?? 0}</span>
                              <span>AI Agents: {parentLicence?.available_licence?.ai_agent ?? 0}</span>
                              <span>
                                AI Live Channels:{' '}
                                {parentLicence?.available_licence?.ai_agent_live_channel ?? 0}
                              </span>
                              <span>CPS: {parentLicence?.available_licence?.cps ?? 0}</span>
                              <span>Mobile: {parentLicence?.available_licence?.mobile ?? 0}</span>
                            </div>
                            <h6 className="licence-available-summary__title mt-3">
                              Used Licence (This Call Center)
                            </h6>
                            <div className="licence-available-summary__row">
                              {showCallCenterUsedCol && (
                                <span>Call Centers: {usedLicence?.role_ccadmin ?? 0}</span>
                              )}
                              <span>Agent: {usedLicence?.role_ccagent ?? 0}</span>
                              <span>Total Team Lead: {usedLicence?.role_teamlead ?? 0}</span>
                              <span>Live Agent: {usedLicence?.live_agent ?? 0}</span>
                              <span>Channels: {usedLicence?.channel ?? 0}</span>
                              <span>AI Agents: {usedLicence?.ai_agent ?? 0}</span>
                              <span>
                                AI Live Channels: {usedLicence?.ai_agent_live_channel ?? 0}
                              </span>
                              <span>CPS: {usedLicence?.cps ?? 0}</span>
                              <span>Mobile: {usedLicence?.mobile ?? 0}</span>
                            </div>
                          </div>
                        ) : null
                      }
                    />

                    <div className="mt-6 flex justify-end">
                      <Button
                        onClick={submitForm}
                        type="button"
                        disabled={
                          buttonDisable || hasLicenceErrors || !!nameError || !!domainError
                        }
                        className="gap-2"
                      >
                        {buttonDisable ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <FaHeadset />
                        )}
                        Update Call Center
                      </Button>
                    </div>
                    </>
                    )}

                    {/* Wizard nav — Back on step > 0, Next until the
                        final step (which owns its own Submit above). */}
                    <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-4">
                      <ShadPageButton
                        type="button"
                        variant="ghost"
                        onClick={stepper.prev}
                        disabled={stepper.current === 0}
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                      </ShadPageButton>
                      {stepper.current < EDIT_CALL_CENTRE_STEPS.length - 1 && (
                        <ShadPageButton type="button" onClick={handleWizardNext}>
                          Next
                          <ArrowRight className="h-4 w-4" />
                        </ShadPageButton>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </ShadcnCard>
        </Col>
      </Row>
    </FillPage>
  );
};

export default CallCentreUpdate;
